import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { adminUpload } from "@/lib/adminUpload";
import { Plus, Trash2, Loader2, Upload, Eye, EyeOff, GripVertical, Image as ImageIcon, FolderPlus, X, Sparkles, Copy, LifeBuoy, CheckSquare, Square, FolderInput, Wrench, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function GalleryAdmin() {
  const [cats, setCats] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [autoCatRunning, setAutoCatRunning] = useState(false);
  const [dedupeRunning, setDedupeRunning] = useState(false);
  const [recoverRunning, setRecoverRunning] = useState(false);
  const [wipeRunning, setWipeRunning] = useState(false);
  const [brokenRunning, setBrokenRunning] = useState(false);
  const [stemDedupeRunning, setStemDedupeRunning] = useState(false);
  const [restoreRunning, setRestoreRunning] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  // Multi-select for shift-click bulk delete
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [lastClickedByCat, setLastClickedByCat] = useState({}); // { catName: photoId }
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [moveTargetOpen, setMoveTargetOpen] = useState(false);
  const fileRefs = useRef({});

  const togglePhotoSelected = (catName, photoId, sortedIdsInCat, e) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const isShift = e?.shiftKey;
      const lastId = lastClickedByCat[catName];
      if (isShift && lastId && lastId !== photoId) {
        const a = sortedIdsInCat.indexOf(lastId);
        const b = sortedIdsInCat.indexOf(photoId);
        if (a !== -1 && b !== -1) {
          const [start, end] = a < b ? [a, b] : [b, a];
          // Determine target state from the just-clicked photo
          const willSelect = !next.has(photoId);
          for (let i = start; i <= end; i++) {
            const id = sortedIdsInCat[i];
            if (willSelect) next.add(id); else next.delete(id);
          }
          return next;
        }
      }
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
    setLastClickedByCat((prev) => ({ ...prev, [catName]: photoId }));
  };

  const selectAllInCat = (catName, ids) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = ids.every((id) => next.has(id));
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const clearSelection = () => { setSelectedIds(new Set()); setLastClickedByCat({}); };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return toast.error("Nothing selected");
    if (!window.confirm(`Delete ${ids.length} selected photo${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBulkDeleting(true);
    try {
      const r = await api.post("/admin/gallery-photos/bulk-delete", { ids }, { timeout: 120000 });
      toast.success(`Deleted ${r.data.deleted} photo${r.data.deleted === 1 ? "" : "s"}`);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };

  const bulkAssign = async (categoryName) => {
    const ids = Array.from(selectedIds);
    const cat = (categoryName || "").trim();
    if (!ids.length) return toast.error("Nothing selected");
    if (!cat) return toast.error("Pick a category");
    setBulkAssigning(true);
    try {
      const r = await api.post(
        "/admin/gallery-photos/bulk-assign-category",
        { ids, category: cat },
        { timeout: 120000 },
      );
      toast.success(`Moved ${r.data.moved} photo${r.data.moved === 1 ? "" : "s"} → ${cat}`);
      setMoveTargetOpen(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    } finally {
      setBulkAssigning(false);
    }
  };

  // Keyboard escape clears selection
  useEffect(() => {
    if (!selectMode) return;
    const onKey = (e) => { if (e.key === "Escape") { clearSelection(); setSelectMode(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectMode]);

  const runRecover = async () => {
    setRecoverRunning(true);
    try {
      // 1) Diagnose first — show user where photos are
      const dbg = await api.get("/admin/maintenance/gallery-debug");
      const c = dbg.data.counts || {};
      const orphan = c.image_uploads_NOT_in_gallery || 0;
      const inGallery = c.gallery_photos || 0;
      const inOrphanColl = c.gallery_images || 0;
      const summary =
        `Diagnostics:\n` +
        `  • gallery_photos (live): ${inGallery}\n` +
        `  • gallery_images (orphan collection): ${inOrphanColl}\n` +
        `  • Image uploads NOT in gallery: ${orphan}\n` +
        `  • uploads from ZIP migration: ${c.uploads_from_zip_upload || 0}\n` +
        `  • uploads from gallery import: ${c.uploads_from_gallery_zip || 0}\n\n`;
      if (orphan === 0 && inOrphanColl === 0) {
        toast.success("Nothing to recover — all uploads are already in the gallery.");
        window.alert(summary + "✓ Nothing to recover.");
        return;
      }
      const ok = window.confirm(
        summary +
        `Recover all ${orphan} orphaned image upload(s) into the public Gallery now?\n` +
        `(WP-generated thumbnails will be skipped automatically.)`
      );
      if (!ok) return;
      // 2) Run boot-style recovery on gallery_images first (legacy)
      if (inOrphanColl > 0) {
        try { await api.post("/admin/maintenance/recover-orphan-gallery"); } catch (_) {}
      }
      // 3) Run the new comprehensive recovery from db.uploads
      const r = await api.post("/admin/maintenance/recover-photos-from-uploads", {}, { timeout: 600000 });
      const d = r.data;
      toast.success(`Recovered ${d.inserted} photo(s) into the gallery (skipped ${d.skipped_thumbnails} thumbnails).`);
      window.alert(
        `Recovery complete:\n` +
        `  • Inserted: ${d.inserted}\n` +
        `  • Skipped thumbnails: ${d.skipped_thumbnails}\n` +
        `  • Already in gallery: ${d.skipped_already_in_gallery}\n` +
        `  • Gallery total now: ${d.gallery_total_after}\n\n` +
        `${d.next_step || ""}`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Recovery failed");
    } finally {
      setRecoverRunning(false);
    }
  };

  const runWipe = async () => {
    if (!window.confirm(
      "Remove all 'Imported' photos from the public gallery?\n\n" +
      "This deletes the gallery entries only. The actual image files stay safely stored on the server, so any hardcoded WP image URLs on other pages keep working.\n\n" +
      "Your original seeded photos (Eid, Family Camp, Iftaar, etc.) and any photos you uploaded manually are NOT touched.\n\n" +
      "Continue?"
    )) return;
    setWipeRunning(true);
    try {
      const r = await api.post("/admin/maintenance/wipe-recovered-gallery", {}, { timeout: 300000 });
      toast.success(`Wiped ${r.data.deleted} recovered photo${r.data.deleted === 1 ? "" : "s"} — gallery total: ${r.data.gallery_total_after}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wipe failed");
    } finally {
      setWipeRunning(false);
    }
  };

  const runRemoveBroken = async () => {
    if (!window.confirm(
      "Remove every gallery photo whose underlying file is missing or 0 bytes?\n\n" +
      "External (http://) URLs will NOT be touched. Internal /api/files/<id> photos pointing at missing or soft-deleted uploads will be deleted.\n\nContinue?"
    )) return;
    setBrokenRunning(true);
    try {
      const r = await api.post("/admin/maintenance/remove-broken-gallery-photos", {}, { timeout: 300000 });
      const d = r.data;
      toast.success(`Removed ${d.deleted_broken} broken photo${d.deleted_broken === 1 ? "" : "s"}.`);
      window.alert(
        `Broken-photo cleanup:\n` +
        `  • Deleted (missing files): ${d.deleted_broken}\n` +
        `  • Kept (file OK): ${d.kept_ok}\n` +
        `  • Kept (external URL): ${d.kept_external_urls}\n` +
        `  • Gallery total now: ${d.gallery_total_after}`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setBrokenRunning(false);
    }
  };

  const runRestore = async () => {
    if (!window.confirm(
      "Restore the original 385-photo seeded gallery (Eid Festivals, Family Camp, Interfaith Iftaar, etc.) from the static seed file?\n\n" +
      "This is IDEMPOTENT — photos already in the gallery will be skipped. Categories are auto-created if missing.\n\n" +
      "Continue?"
    )) return;
    setRestoreRunning(true);
    try {
      const r = await api.post("/admin/maintenance/restore-gallery-from-seed", {}, { timeout: 300000 });
      const d = r.data;
      const top = Object.entries(d.per_category || {}).slice(0, 8).map(([k,v]) => `${k} (${v})`).join(", ");
      toast.success(`Restored ${d.inserted} photos — gallery total: ${d.gallery_total_after}`);
      window.alert(
        `Gallery restore complete:\n` +
        `  • Inserted: ${d.inserted}\n` +
        `  • Skipped (already in gallery): ${d.skipped_already_in_gallery}\n` +
        `  • Gallery total now: ${d.gallery_total_after}\n\n` +
        (top ? `Top categories: ${top}` : "")
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally {
      setRestoreRunning(false);
    }
  };

  const runStemDedupe = async () => {
    setStemDedupeRunning(true);
    try {
      const r = await api.post("/admin/maintenance/dedupe-by-filename-stem?dry_run=true", {}, { timeout: 300000 });
      const d = r.data;
      if (d.total_duplicates === 0) {
        toast.success("No filename-stem duplicates found 🎉");
        return;
      }
      const sample = (d.clusters_preview || []).slice(0, 6).map((c) =>
        `   • ${c.count}× "${c.stem}" (keep ${(c.keep_caption || c.keep_id).slice(0, 32)})`
      ).join("\n");
      const ok = window.confirm(
        `Found ${d.total_duplicates} visual duplicate(s) across ${d.duplicate_clusters} stem cluster(s)\n` +
        `(WP variants like -qnfxz... / -orig / size suffixes grouped together).\n\n` +
        `First 6 clusters:\n${sample}\n\n` +
        `Delete all duplicates and keep the OLDEST copy of each? (Recommended.)`
      );
      if (!ok) return;
      const r2 = await api.post("/admin/maintenance/dedupe-by-filename-stem?dry_run=false", {}, { timeout: 300000 });
      toast.success(`Removed ${r2.data.deleted} visual duplicates — gallery total: ${r2.data.gallery_total_after}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Visual dedupe failed");
    } finally {
      setStemDedupeRunning(false);
    }
  };

  const runAutoCategorize = async (overwrite = false) => {
    const msg = overwrite
      ? "Re-assign EVERY photo's category by inferring from its filename / path? This will overwrite manual category choices."
      : "Auto-assign categories to photos that don't have one yet, based on their filename / file path?";
    if (!window.confirm(msg)) return;
    setAutoCatRunning(true);
    try {
      const r = await api.post(`/admin/maintenance/auto-categorize-gallery${overwrite ? "?overwrite=true" : ""}`);
      const d = r.data;
      const cats = Object.entries(d.categories_assigned || {})
        .slice(0, 5)
        .map(([k, v]) => `${k} (${v})`)
        .join(", ");
      toast.success(`Categorized ${d.updated} photos. Top: ${cats || "—"}. Skipped: ${d.skipped_no_match} no-match, ${d.skipped_already_categorized} already set.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-categorize failed");
    } finally { setAutoCatRunning(false); }
  };

  const runDedupe = async () => {
    if (!window.confirm("Step 1 — Scan the gallery for byte-identical duplicate photos? This is a DRY RUN — nothing is deleted. You'll see a summary first, then can confirm the actual cleanup.")) return;
    setDedupeRunning(true);
    try {
      const r = await api.post("/admin/maintenance/dedupe-gallery?dry_run=true", {}, { timeout: 600000 });
      const d = r.data;
      if (d.total_duplicates === 0) {
        toast.success("No duplicates found 🎉");
        return;
      }
      const sample = (d.clusters_preview || []).slice(0, 5).map((c) =>
        `   • ${c.count} copies of ${c.size_bytes} bytes (keep "${c.keep_caption || c.keep_id.slice(0, 8)}")`
      ).join("\n");
      const ok = window.confirm(
        `Found ${d.total_duplicates} duplicate(s) across ${d.duplicate_clusters} cluster(s).\n` +
        `Recoverable: ${d.mb_recoverable} MB.\n\n` +
        `First 5 clusters:\n${sample}\n\n` +
        `Delete the duplicates and keep the OLDEST copy of each? (Manual captions/categories on the kept copy survive.)`
      );
      if (!ok) {
        toast(`${d.total_duplicates} duplicates found — cancelled, nothing deleted.`);
        return;
      }
      const r2 = await api.post("/admin/maintenance/dedupe-gallery?dry_run=false", {}, { timeout: 600000 });
      const d2 = r2.data;
      toast.success(`Cleaned up — deleted ${d2.deleted_gallery_rows} duplicate gallery rows (${d2.mb_recoverable} MB freed)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dedupe failed");
    } finally { setDedupeRunning(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get("/admin/gallery-categories"),
        api.get("/admin/gallery-photos/"),
      ]);
      setCats(c.data);
      setPhotos(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const uploadOne = adminUpload;

  const onFilesPicked = async (catName, filesList) => {
    const files = Array.from(filesList || []);
    if (!files.length) return;
    setUploadingFor(catName);
    let added = 0;
    for (const f of files) {
      try {
        const url = await uploadOne(f);
        await api.post("/admin/gallery-photos/", {
          image_url: url,
          caption: "",
          category: catName,
          order: photos.filter((p) => p.category === catName).length + added,
          is_published: true,
        });
        added++;
      } catch (e) { toast.error(`Upload failed: ${e.message}`); }
    }
    toast.success(`Uploaded ${added} photo${added === 1 ? "" : "s"} to ${catName}`);
    setUploadingFor(null);
    await load();
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Delete this photo?")) return;
    try { await api.delete(`/admin/gallery-photos/${id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  const togglePhoto = async (p) => {
    try { await api.put(`/admin/gallery-photos/${p.id}`, { ...p, is_published: !p.is_published }); await load(); }
    catch { toast.error("Could not toggle"); }
  };

  const movePhoto = async (p, targetCategory) => {
    const cat = (targetCategory || "").trim();
    if (!cat || cat === p.category) return;
    try {
      await api.post(
        "/admin/gallery-photos/bulk-assign-category",
        { ids: [p.id], category: cat },
      );
      toast.success(`Moved to ${cat}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
    }
  };

  const saveCaption = async (p, caption) => {
    try { await api.put(`/admin/gallery-photos/${p.id}`, { ...p, caption }); toast.success("Saved"); }
    catch { toast.error("Save failed"); }
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    try {
      await api.post("/admin/gallery-categories", {
        name: newCatName.trim(), order: cats.length, is_published: true,
      });
      setNewCatName(""); setNewCatOpen(false);
      toast.success("Category added");
      await load();
    } catch { toast.error("Could not add"); }
  };

  const deleteCategory = async (cat) => {
    const count = photos.filter((p) => p.category === cat.name).length;
    if (count > 0) return toast.error(`Move or delete the ${count} photos in this category first`);
    if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    try { await api.delete(`/admin/gallery-categories/${cat.id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  if (loading) return <div className="text-ink/40">Loading…</div>;

  const sortedCats = [...cats].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="admin-gallery-page">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Media</div>
          <h1 className="font-serif text-4xl mt-2 text-ink">Photo Gallery</h1>
          <p className="mt-2 text-ink/60 text-sm max-w-2xl">
            Upload photos into categories. Drag-drop or click the upload button on each category.
            Photos appear publicly at <a href="/gallery" className="text-navy underline" target="_blank" rel="noreferrer">/gallery</a>.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {selectMode ? (
            <>
              <span data-testid="gal-select-count" className="text-sm font-semibold text-ink/70 px-3 py-2 rounded-full bg-cream border border-sand">
                {selectedIds.size} selected
              </span>
              <div className="relative">
                <button
                  onClick={() => setMoveTargetOpen((v) => !v)}
                  disabled={bulkAssigning || bulkDeleting || selectedIds.size === 0}
                  data-testid="gal-bulk-move"
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-60"
                >
                  {bulkAssigning ? <Loader2 size={14} className="animate-spin" /> : <FolderInput size={14} />}
                  Move to category
                </button>
                {moveTargetOpen && selectedIds.size > 0 && (
                  <div className="absolute right-0 top-full mt-2 w-72 rounded-2xl border border-sand bg-white shadow-2xl z-30 overflow-hidden" data-testid="gal-bulk-move-menu">
                    <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-ink/55 font-semibold border-b border-sand bg-cream/50">
                      Move {selectedIds.size} photo{selectedIds.size === 1 ? "" : "s"} to…
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {[...cats].sort((a, b) => (a.order || 0) - (b.order || 0)).map((c) => (
                        <button
                          key={c.id}
                          onClick={() => bulkAssign(c.name)}
                          data-testid={`gal-bulk-move-to-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                          className="w-full text-left px-4 py-2.5 text-sm text-ink hover:bg-cream/60 flex items-center gap-2"
                        >
                          <FolderInput size={12} className="text-ink/40" /> {c.name}
                          <span className="ml-auto text-[11px] text-ink/40">{photos.filter((p) => p.category === c.name).length}</span>
                        </button>
                      ))}
                      <div className="border-t border-sand" />
                      <button
                        onClick={() => {
                          const name = window.prompt("New category name:");
                          if (name && name.trim()) bulkAssign(name.trim());
                        }}
                        data-testid="gal-bulk-move-new"
                        className="w-full text-left px-4 py-2.5 text-sm text-navy font-semibold hover:bg-cream/60 flex items-center gap-2"
                      >
                        <FolderPlus size={12} /> New category…
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={bulkDelete}
                disabled={bulkDeleting || bulkAssigning || selectedIds.size === 0}
                data-testid="gal-bulk-delete"
                className="inline-flex items-center gap-2 rounded-full bg-rose-600 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-rose-700 disabled:opacity-60"
              >
                {bulkDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                Delete selected
              </button>
              <button
                onClick={() => { clearSelection(); setSelectMode(false); setMoveTargetOpen(false); }}
                disabled={bulkDeleting || bulkAssigning}
                data-testid="gal-select-cancel"
                className="inline-flex items-center gap-2 rounded-full bg-ink/10 text-ink px-4 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-ink/20"
              >
                <X size={14} /> Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setSelectMode(true)}
              data-testid="gal-select-mode"
              className="inline-flex items-center gap-2 rounded-full bg-ink/5 text-ink px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-ink/10"
            >
              <CheckSquare size={14} /> Select
            </button>
          )}
          <button
            onClick={runWipe}
            disabled={wipeRunning || recoverRunning || autoCatRunning || dedupeRunning || stemDedupeRunning || brokenRunning || restoreRunning}
            data-testid="gal-wipe-recovered"
            title="Remove the 1,615 'Imported' photos from the public gallery (the WP zip uploads stay safely stored — only their gallery entries are removed)"
            className="inline-flex items-center gap-2 rounded-full bg-rose-700 text-white px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-rose-800 disabled:opacity-60"
          >
            {wipeRunning ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            Remove imported
          </button>
          <button
            onClick={() => setNewCatOpen(true)}
            data-testid="gal-new-cat"
            className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900"
          >
            <FolderPlus size={14} /> New category
          </button>
          {/* Advanced tools — tucked behind a dropdown so the toolbar stays clean.
              These actions are rare or destructive; hide them by default. */}
          <div className="relative">
            <button
              onClick={() => setToolsOpen((v) => !v)}
              data-testid="gal-tools"
              className="inline-flex items-center gap-2 rounded-full bg-ink/5 text-ink px-5 py-2.5 text-sm font-semibold uppercase tracking-wider hover:bg-ink/10"
            >
              <Wrench size={14} /> Tools <ChevronDown size={14} className={`transition-transform ${toolsOpen ? "rotate-180" : ""}`} />
            </button>
            {toolsOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setToolsOpen(false)} />
                <div className="absolute right-0 top-full mt-2 w-72 rounded-2xl border border-sand bg-white shadow-2xl z-30 overflow-hidden" data-testid="gal-tools-menu">
                  <div className="px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-ink/55 font-semibold border-b border-sand bg-cream/50">
                    Maintenance
                  </div>
                  <button
                    onClick={() => { setToolsOpen(false); runRestore(); }}
                    disabled={restoreRunning}
                    data-testid="gal-restore"
                    className="w-full text-left px-4 py-3 text-sm text-ink hover:bg-cream/60 flex items-start gap-3 disabled:opacity-50"
                  >
                    <LifeBuoy size={14} className="text-gold mt-0.5 shrink-0" />
                    <span>
                      <strong className="block">Restore default gallery</strong>
                      <span className="text-[11px] text-ink/55">Re-seed the original 385 photos (idempotent)</span>
                    </span>
                  </button>
                  <button
                    onClick={() => { setToolsOpen(false); runAutoCategorize(false); }}
                    disabled={autoCatRunning}
                    data-testid="gal-auto-categorize"
                    className="w-full text-left px-4 py-3 text-sm text-ink hover:bg-cream/60 flex items-start gap-3 disabled:opacity-50"
                  >
                    <Sparkles size={14} className="text-gold mt-0.5 shrink-0" />
                    <span>
                      <strong className="block">Auto-categorize</strong>
                      <span className="text-[11px] text-ink/55">Group photos by filename / folder path</span>
                    </span>
                  </button>
                  <button
                    onClick={() => { setToolsOpen(false); runDedupe(); }}
                    disabled={dedupeRunning}
                    data-testid="gal-dedupe"
                    className="w-full text-left px-4 py-3 text-sm text-ink hover:bg-cream/60 flex items-start gap-3 disabled:opacity-50"
                  >
                    <Copy size={14} className="text-rose-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="block">Find exact duplicates</strong>
                      <span className="text-[11px] text-ink/55">Byte-hash dedupe</span>
                    </span>
                  </button>
                  <button
                    onClick={() => { setToolsOpen(false); runStemDedupe(); }}
                    disabled={stemDedupeRunning}
                    data-testid="gal-stem-dedupe"
                    className="w-full text-left px-4 py-3 text-sm text-ink hover:bg-cream/60 flex items-start gap-3 disabled:opacity-50"
                  >
                    <Copy size={14} className="text-rose-500 mt-0.5 shrink-0" />
                    <span>
                      <strong className="block">Visual dedupe</strong>
                      <span className="text-[11px] text-ink/55">Catch WP filename variants</span>
                    </span>
                  </button>
                  <div className="border-t border-sand" />
                  <div className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-rose-500 font-semibold bg-rose-50/40">
                    Danger zone
                  </div>
                  <button
                    onClick={() => { setToolsOpen(false); runRemoveBroken(); }}
                    disabled={brokenRunning}
                    data-testid="gal-remove-broken"
                    className="w-full text-left px-4 py-3 text-sm text-ink hover:bg-rose-50 flex items-start gap-3 disabled:opacity-50"
                  >
                    <ImageIcon size={14} className="text-amber-600 mt-0.5 shrink-0" />
                    <span>
                      <strong className="block">Remove broken photos</strong>
                      <span className="text-[11px] text-ink/55">Delete photos with missing files. Use with caution.</span>
                    </span>
                  </button>
                  <button
                    onClick={() => { setToolsOpen(false); runRecover(); }}
                    disabled={recoverRunning}
                    data-testid="gal-recover"
                    className="w-full text-left px-4 py-3 text-sm text-ink hover:bg-rose-50 flex items-start gap-3 disabled:opacity-50"
                  >
                    <LifeBuoy size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                    <span>
                      <strong className="block">Recover from uploads</strong>
                      <span className="text-[11px] text-ink/55">Pull all stored uploads into the gallery (re-creates Imported)</span>
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {newCatOpen && (
        <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4" onClick={() => setNewCatOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-3xl bg-white border border-sand p-7">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl">New category</h3>
              <button onClick={() => setNewCatOpen(false)}><X size={18} /></button>
            </div>
            <input
              type="text" placeholder="e.g. Youth Retreat 2026"
              value={newCatName} onChange={(e) => setNewCatName(e.target.value)}
              data-testid="gal-new-cat-name"
              className="w-full px-4 py-3 rounded-xl border border-sand focus:outline-none focus:border-navy"
            />
            <button onClick={addCategory} data-testid="gal-new-cat-save" className="mt-4 w-full rounded-full bg-navy text-cream py-3 text-sm font-semibold uppercase tracking-wider hover:bg-navy-900">Add category</button>
          </div>
        </div>
      )}

      <div className="space-y-8">
        {sortedCats.map((cat) => {
          const inCat = photos.filter((p) => p.category === cat.name).sort((a, b) => (a.order || 0) - (b.order || 0));
          const isUploading = uploadingFor === cat.name;
          const idsInCat = inCat.map((p) => p.id);
          const allInCatSelected = idsInCat.length > 0 && idsInCat.every((id) => selectedIds.has(id));
          const someInCatSelected = idsInCat.some((id) => selectedIds.has(id));
          return (
            <section key={cat.id} data-testid={`gal-cat-${cat.name.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-3xl bg-white border border-sand p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <GripVertical className="text-ink/30" size={14} />
                    <h2 className="font-serif text-2xl text-ink">{cat.name}</h2>
                    <span className="text-xs text-ink/55">({inCat.length} photo{inCat.length === 1 ? "" : "s"})</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selectMode && idsInCat.length > 0 && (
                    <button
                      onClick={() => selectAllInCat(cat.name, idsInCat)}
                      data-testid={`gal-select-all-${cat.id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-ink/5 text-ink px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider hover:bg-ink/10"
                      title={allInCatSelected ? "Unselect all in category" : "Select all in category"}
                    >
                      {allInCatSelected ? <CheckSquare size={12} /> : <Square size={12} />}
                      {allInCatSelected ? "Unselect all" : someInCatSelected ? "Select rest" : "Select all"}
                    </button>
                  )}
                  <input
                    type="file" multiple accept="image/*"
                    ref={(el) => { fileRefs.current[cat.id] = el; }}
                    onChange={(e) => onFilesPicked(cat.name, e.target.files)}
                    className="hidden"
                    data-testid={`gal-upload-input-${cat.id}`}
                  />
                  <button
                    disabled={isUploading || selectMode}
                    onClick={() => fileRefs.current[cat.id]?.click()}
                    data-testid={`gal-upload-btn-${cat.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-4 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-navy-900 disabled:opacity-60"
                  >
                    {isUploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    Upload photos
                  </button>
                  <button onClick={() => deleteCategory(cat)} data-testid={`gal-cat-delete-${cat.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-md" title="Delete category">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {inCat.length === 0 ? (
                <div className="py-10 text-center text-ink/40 italic border-2 border-dashed border-sand rounded-2xl">
                  No photos yet — click "Upload photos" to add.
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {inCat.map((p) => (
                    <PhotoTile
                      key={p.id}
                      p={p}
                      onDelete={() => deletePhoto(p.id)}
                      onToggle={() => togglePhoto(p)}
                      onSaveCaption={(c) => saveCaption(p, c)}
                      onMove={(target) => movePhoto(p, target)}
                      cats={cats}
                      selectMode={selectMode}
                      selected={selectedIds.has(p.id)}
                      onSelectClick={(e) => togglePhotoSelected(cat.name, p.id, idsInCat, e)}
                    />
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {cats.length === 0 && (
          <div className="py-16 text-center text-ink/45 italic rounded-3xl border border-dashed border-sand">
            <ImageIcon className="mx-auto text-gold mb-3" size={28} />
            No categories yet — click "New category".
          </div>
        )}
      </div>
    </div>
  );
}

function PhotoTile({ p, onDelete, onToggle, onSaveCaption, onMove, cats, selectMode, selected, onSelectClick }) {
  const [caption, setCaption] = useState(p.caption || "");
  const [moveOpen, setMoveOpen] = useState(false);
  return (
    <div
      data-testid={`photo-tile-${p.id}`}
      className={`relative rounded-xl overflow-hidden border group ${selected ? "border-rose-500 ring-2 ring-rose-400" : "border-sand"} ${selectMode ? "cursor-pointer select-none" : ""}`}
      onClick={selectMode ? onSelectClick : undefined}
    >
      <img
        src={p.image_url}
        alt={p.caption || ""}
        draggable={false}
        className={`w-full aspect-square object-cover transition-opacity ${selectMode && !selected ? "opacity-70" : ""}`}
      />
      {selectMode && (
        <div className={`absolute top-2 left-2 w-6 h-6 rounded-md flex items-center justify-center ${selected ? "bg-rose-600 text-white" : "bg-cream/90 text-ink/70 border border-sand"}`}>
          {selected ? <CheckSquare size={14} /> : <Square size={14} />}
        </div>
      )}
      {!p.is_published && (
        <div className="absolute top-2 right-2 text-[9px] uppercase tracking-wider bg-ink/70 text-cream px-2 py-0.5 rounded">Hidden</div>
      )}
      {!selectMode && (
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); setMoveOpen((v) => !v); }}
            data-testid={`photo-move-${p.id}`}
            title="Move to another category"
            className="w-7 h-7 rounded-md bg-emerald-600/90 text-white flex items-center justify-center hover:bg-emerald-700"
          >
            <FolderInput size={12} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid={`photo-toggle-${p.id}`} className="w-7 h-7 rounded-md bg-cream/90 text-navy flex items-center justify-center hover:bg-cream">
            {p.is_published ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(); }} data-testid={`photo-delete-${p.id}`} className="w-7 h-7 rounded-md bg-red-500/90 text-white flex items-center justify-center hover:bg-red-600">
            <Trash2 size={12} />
          </button>
        </div>
      )}
      {moveOpen && !selectMode && (
        <>
          <div className="fixed inset-0 z-20" onClick={(e) => { e.stopPropagation(); setMoveOpen(false); }} />
          <div className="absolute top-10 right-2 w-60 max-h-72 overflow-y-auto rounded-xl border border-sand bg-white shadow-xl z-30" onClick={(e) => e.stopPropagation()} data-testid={`photo-move-menu-${p.id}`}>
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-ink/55 font-semibold border-b border-sand bg-cream/50">Move to…</div>
            {[...(cats || [])]
              .filter((c) => c.name !== p.category)
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((c) => (
                <button
                  key={c.id}
                  onClick={() => { onMove?.(c.name); setMoveOpen(false); }}
                  data-testid={`photo-move-to-${p.id}-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="w-full text-left px-3 py-2 text-sm text-ink hover:bg-cream/60 flex items-center gap-2"
                >
                  <FolderInput size={11} className="text-ink/40" /> {c.name}
                </button>
              ))}
            <div className="border-t border-sand" />
            <button
              onClick={() => {
                const name = window.prompt("New category name:");
                if (name && name.trim()) { onMove?.(name.trim()); setMoveOpen(false); }
              }}
              className="w-full text-left px-3 py-2 text-sm text-navy font-semibold hover:bg-cream/60 flex items-center gap-2"
            >
              <FolderPlus size={11} /> New category…
            </button>
          </div>
        </>
      )}
      <input
        type="text"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        onBlur={() => { if (caption !== p.caption) onSaveCaption(caption); }}
        onClick={(e) => e.stopPropagation()}
        disabled={selectMode}
        placeholder="Caption…"
        data-testid={`photo-caption-${p.id}`}
        className="w-full px-2 py-1.5 text-[11px] border-t border-sand focus:outline-none disabled:bg-cream/40"
      />
    </div>
  );
}
